import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { usePromotionClaims } from "@/hooks/firestore/user/usePromotionClaims";
import {
  calculateDepositBonusAmountNumber,
  extractPendingDepositBonusClaim,
} from "types/PromotionTypes";
import { ReactComponent as IconDepositMatch } from "../assets/icon-deposit-match.svg";
import { Countdown } from "@/features/shell/components/shared/Countdown";
import { Box } from "@picklebet/ui";
import { ShinyText } from "@picklebet/ui";
import { constructCurrency } from "common/currency";
import { motion } from "motion/react";
import type { CurrencyType } from "@/types/BetTypes";
import cx from "classnames";

import * as styles from "./DepositBonus.module.scss";

const getSlideBoxProgress = (progress: number, min: number, max: number) => {
  return progress > 0 && progress < min ? min : progress > max ? max : progress;
};

const formatedCurrency = (
  amount: number | undefined,
  currencyType: CurrencyType | string,
  showDecimals = true,
) =>
  constructCurrency(amount || 0, {
    currencyType,
    showDecimals,
  });

const SlideBox = ({
  children,
  progress,
}: { children: React.ReactNode; progress: number }) => {
  const rdmBarSlideBoxRef = useRef<HTMLDivElement | null>(null);
  const slideBoxContentRef = useRef<HTMLDivElement | null>(null);
  const [slideBoxMin, setSlideBoxMin] = useState(25);
  const [slideBoxMax, setSlideBoxMax] = useState(75);

  const slideBoxProgress = useMemo(
    () => getSlideBoxProgress(progress, slideBoxMin, slideBoxMax),
    [slideBoxMin, slideBoxMax, progress],
  );

  useEffect(() => {
    if (!slideBoxContentRef.current || !rdmBarSlideBoxRef.current) return;

    const update = () => {
      const pillWidth =
        slideBoxContentRef?.current?.getBoundingClientRect().width || 0;
      const slideWidth =
        rdmBarSlideBoxRef?.current?.getBoundingClientRect().width || 0;

      const distance = Math.floor((pillWidth / 2 / slideWidth) * 100);
      setSlideBoxMin(distance);
      setSlideBoxMax(100 - distance);
    };

    const pillObserver = new ResizeObserver(update);
    const slideObserver = new ResizeObserver(update);

    pillObserver.observe(slideBoxContentRef.current);
    slideObserver.observe(rdmBarSlideBoxRef.current);

    update();

    return () => {
      pillObserver.disconnect();
      slideObserver.disconnect();
    };
  }, [slideBoxContentRef, rdmBarSlideBoxRef]);

  return (
    <div className={styles.rdmBarSlideBox} ref={rdmBarSlideBoxRef}>
      <motion.div
        initial={{ x: `${progress}%`, opacity: 0 }}
        animate={{ x: `${slideBoxProgress}%`, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeIn" }}
      >
        <div className={styles.rdmBarSlideBoxTrigger} ref={slideBoxContentRef}>
          {children}
        </div>
      </motion.div>
    </div>
  );
};

type DepositBonusProps = {
  /** renders the offer as a plain sentence instead of the card */
  basic?: boolean;
  /** scales the card down for use inside a modal */
  compact?: boolean;
  /** the in-progress deposit, in cents, used for the rolling match preview */
  depositAmount?: number;
};

const DepositBonus = ({
  basic = false,
  compact = false,
  depositAmount = 0,
}: DepositBonusProps) => {
  const { claims: promotionClaims } = usePromotionClaims();
  const pendingDepositClaim = extractPendingDepositBonusClaim(promotionClaims);

  const fiatCurrency = useFiatCurrency();

  const percentage = pendingDepositClaim?.matchPercentage || 100;
  const isCumulative = pendingDepositClaim?.isCumulative || false;

  if (!pendingDepositClaim || pendingDepositClaim?.currency !== fiatCurrency) {
    return null;
  }

  const formattedMaxDepositAmount =
    pendingDepositClaim?.amount && pendingDepositClaim?.currency
      ? formatedCurrency(
          pendingDepositClaim?.amount,
          pendingDepositClaim?.currency,
        )
      : null;

  const claimProgress =
    pendingDepositClaim?.amountClaimed && pendingDepositClaim?.amount
      ? ((pendingDepositClaim?.amountClaimed || 0) /
          pendingDepositClaim?.amount) *
        100
      : 0;

  const depositMatchAmount = calculateDepositBonusAmountNumber(
    depositAmount / 100,
    pendingDepositClaim,
  );

  // should not be greater than remaining claimProgress %
  const depositProgress =
    depositMatchAmount &&
    pendingDepositClaim?.amount &&
    pendingDepositClaim?.amountRemaining
      ? (Math.min(depositMatchAmount, pendingDepositClaim.amountRemaining) /
          pendingDepositClaim?.amount) *
        100
      : 0;

  const progress = depositProgress + claimProgress;

  return basic ? (
    `We'll match ${percentage && percentage !== 100 ? `${pendingDepositClaim.matchPercentage}% of` : ""}
    ${isCumulative ? "all deposits" : "your next deposit"}, up to ${isCumulative ? "a total of" : ""} ${formattedMaxDepositAmount} in bonus cash`
  ) : (
    <Box
      hideHeader
      hasPadding={false}
      className={cx(styles.bonusBox, compact && styles.compact)}
    >
      <div>
        <div className={styles.depositBonusHeader}>
          <div>
            <div className={styles.leftClamp}>
              <IconDepositMatch />
            </div>
          </div>
          <div className={styles.depositBonusHeaderContent}>
            <div className={styles.title}>
              {isCumulative ? `Rolling ` : ""}Deposit Match
            </div>
            <div className={styles.subTitle}>
              We'll match{" "}
              {percentage && percentage !== 100 && (
                <>
                  <em>
                    <ShinyText
                      duration={1.2}
                      repeatDelay={1.1}
                      className={styles.shinyText}
                    >
                      {pendingDepositClaim.matchPercentage}%
                    </ShinyText>
                  </em>{" "}
                  of{" "}
                </>
              )}
              {isCumulative ? "all deposits" : "your next deposit"}, up to{" "}
              {isCumulative ? "a total of " : " "}
              <em>
                <ShinyText
                  duration={1.2}
                  delay={0.45}
                  repeatDelay={1.1}
                  className={styles.shinyText}
                >
                  {formattedMaxDepositAmount}
                </ShinyText>
              </em>{" "}
              in{" "}
              <em>
                <ShinyText
                  duration={1.1}
                  delay={0.9}
                  repeatDelay={1.2}
                  className={styles.shinyText}
                >
                  bonus cash
                </ShinyText>
              </em>
            </div>
          </div>

          <div className={styles.rightClamp}>
            {pendingDepositClaim?.expireAt ? (
              <Countdown scheduledStartTime={pendingDepositClaim.expireAt} />
            ) : (
              <div className={styles.clampSpacer}></div>
            )}
          </div>
        </div>
        {isCumulative && (
          <div className={styles.rdmContainer}>
            {depositAmount ? (
              <SlideBox progress={progress}>
                <div className={styles.rdmBarPill}>
                  <span className={styles.yellowText}>
                    <ShinyText
                      className={styles.shinyText}
                      duration={1.2}
                      repeatDelay={1.1}
                    >
                      +
                      {formatedCurrency(
                        depositMatchAmount,
                        pendingDepositClaim?.currency,
                      )}
                    </ShinyText>
                  </span>{" "}
                  with{" "}
                  {depositAmount <= 99999900 ? (
                    <span className={styles.greenText}>
                      {formatedCurrency(
                        depositAmount,
                        pendingDepositClaim?.currency,
                      )}
                    </span>
                  ) : (
                    "your"
                  )}{" "}
                  deposit
                </div>
              </SlideBox>
            ) : null}

            <div className={styles.rdmBar}>
              <motion.div
                className={styles.rdmBarClaimed}
                initial={{ width: 0 }}
                animate={{
                  width: `${claimProgress}%`,
                }}
                transition={{ duration: 0.3, ease: "easeIn" }}
              />
              <motion.div
                className={styles.rdmBarPendingDeposit}
                initial={{ width: 0 }}
                animate={{
                  width: `${depositProgress}%`,
                }}
                transition={{ duration: 0.3, ease: "easeIn" }}
              />
            </div>

            <div className={styles.rdmKey}>
              <div>
                <span className={styles.yellowText}>
                  {formatedCurrency(
                    pendingDepositClaim?.amountClaimed,
                    pendingDepositClaim?.currency,
                  )}
                </span>{" "}
                Used
              </div>
              <div>
                <span className={styles.yellowText}>
                  {formatedCurrency(
                    pendingDepositClaim?.amount,
                    pendingDepositClaim?.currency,
                  )}
                </span>{" "}
                Total
              </div>
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};

export { DepositBonus };
